import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Star,
  MessageSquare,
  Briefcase,
  ChevronDown,
  User,
  CheckCircle,
  Loader2,
  UserPlus,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { api, type Review } from '../lib/api';
import { useAppStore } from '../stores/app-store';
import { notify } from '../lib/toast';
import { ReviewCard, HireModal, PortfolioGallery } from './marketplace';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTitle,
} from './ui/responsive-modal';
import { DefaultAvatar } from './default-avatar';
import { Rise } from './ui/rise';
import { PageShell } from './ui/page-shell';
import { EmptyState } from './ui/empty-state';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Language → ISO 3166-1 alpha-2 flag code mapping
const LANGUAGE_FLAG_MAP: Record<string, string> = {
  português: 'br',
  portuguese: 'br',
  'pt-br': 'br',
  'pt-pt': 'pt',
  pt: 'br',
  inglês: 'us',
  english: 'us',
  en: 'us',
  'en-us': 'us',
  'en-gb': 'gb',
  espanhol: 'es',
  spanish: 'es',
  español: 'es',
  es: 'es',
  francês: 'fr',
  french: 'fr',
  français: 'fr',
  fr: 'fr',
  alemão: 'de',
  german: 'de',
  deutsch: 'de',
  de: 'de',
  italiano: 'it',
  italian: 'it',
  it: 'it',
  japonês: 'jp',
  japanese: 'jp',
  ja: 'jp',
  chinês: 'cn',
  chinese: 'cn',
  zh: 'cn',
  russo: 'ru',
  russian: 'ru',
  ru: 'ru',
  árabe: 'sa',
  arabic: 'sa',
  ar: 'sa',
  holandês: 'nl',
  dutch: 'nl',
  nl: 'nl',
  coreano: 'kr',
  korean: 'kr',
  ko: 'kr',
  polonês: 'pl',
  polish: 'pl',
  pl: 'pl',
};

function getLanguageFlag(lang: string): string | null {
  return LANGUAGE_FLAG_MAP[lang.toLowerCase().trim()] ?? null;
}

// Renders flag icons for a list of languages
function LanguageFlags({ languages, max = 3 }: { languages: string[]; max?: number }) {
  const displayed = languages.slice(0, max);
  const rest = languages.length - max;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {displayed.map((lang) => {
        const code = getLanguageFlag(lang);
        return (
          <span key={lang} title={lang} className="flex items-center gap-1">
            {code ? (
              <span
                className={`fi fi-${code}`}
                style={{ width: '18px', height: '13px', borderRadius: '2px', display: 'inline-block' }}
              />
            ) : (
              <span className="text-[10px] font-mono text-muted-foreground border border-border px-1 rounded">
                {lang.slice(0, 2).toUpperCase()}
              </span>
            )}
          </span>
        );
      })}
      {rest > 0 && (
        <span className="text-[10px] font-mono text-muted-foreground">+{rest}</span>
      )}
    </div>
  );
}

// Types
interface Professional {
  id: string;
  userId: string;
  type: string;
  title: string;
  bio?: string;
  hourlyRate?: number;
  rating: number;
  reviewCount: number;
  isAvailable: boolean;
  specialties: string[];
  languages: string[];
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
}

type ProfessionalType =
  | 'all'
  | 'editor'
  | 'designer'
  | 'proofreader'
  | 'ghostwriter'
  | 'translator'
  | 'beta_reader';

const professionalTypes: { value: ProfessionalType; label: string }[] = [
  { value: 'all', label: 'Todos os Tipos' },
  { value: 'editor', label: 'Editor' },
  { value: 'designer', label: 'Designer de Capa' },
  { value: 'proofreader', label: 'Revisor' },
  { value: 'ghostwriter', label: 'Ghostwriter' },
  { value: 'translator', label: 'Tradutor' },
  { value: 'beta_reader', label: 'Beta Reader' },
];

// i18n key suffixes per professional type, used to translate `professionalTypes` labels at render time.
const PROFESSIONAL_TYPE_I18N_KEYS: Record<ProfessionalType, string> = {
  all: 'typeAll',
  editor: 'typeEditor',
  designer: 'typeDesigner',
  proofreader: 'typeProofreader',
  ghostwriter: 'typeGhostwriter',
  translator: 'typeTranslator',
  beta_reader: 'typeBetaReader',
};

// Helper to format currency
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

// Helper to get rating stars
function getRatingStars(rating: number): number {
  return rating / 100;
}

// Avatar initials helper
function getInitials(name: string): string {
  return (
    name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'P'
  );
}

// uni-av gradients (mock: c1 blue · c2 green · c3 purple · c4 amber) — cycled by card index.
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#7EA4F5,#5B8DEF)',
  'linear-gradient(135deg,var(--aurora),#2E9E8A)',
  'linear-gradient(135deg,#C4ABFC,#A78BFA)',
  'linear-gradient(135deg,var(--ember-hot),var(--ember))',
];

function getAvatarGradient(index: number): string {
  return AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
}

// uni-type chip colors (mock: ut-p blue · ut-l green · ut-o purple), grouped by profession family.
const TYPE_CHIP_STYLES: Record<string, { color: string; bg: string; ring: string }> = {
  editor: { color: '#9DBCF9', bg: 'rgba(91,141,239,.12)', ring: 'rgba(91,141,239,.35)' },
  translator: { color: '#9DBCF9', bg: 'rgba(91,141,239,.12)', ring: 'rgba(91,141,239,.35)' },
  designer: { color: '#C9B4FD', bg: 'rgba(167,139,250,.12)', ring: 'rgba(167,139,250,.35)' },
  ghostwriter: { color: '#C9B4FD', bg: 'rgba(167,139,250,.12)', ring: 'rgba(167,139,250,.35)' },
  proofreader: { color: '#7FE7C2', bg: 'rgba(52,211,153,.1)', ring: 'rgba(52,211,153,.35)' },
  beta_reader: { color: '#7FE7C2', bg: 'rgba(52,211,153,.1)', ring: 'rgba(52,211,153,.35)' },
};

function getTypeChipStyle(type: string) {
  return TYPE_CHIP_STYLES[type] ?? TYPE_CHIP_STYLES.editor;
}

function getProfessionalTypeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  type: string
): string {
  const entry = professionalTypes.find((pt) => pt.value === type);
  if (!entry) return type;
  return t(`professionalsView.${PROFESSIONAL_TYPE_I18N_KEYS[entry.value]}`, entry.label);
}

// Professional Card Component
function ProfessionalCard({
  professional,
  index,
  onContact,
  onViewDetails,
  isContacting,
}: {
  professional: Professional;
  index: number;
  onContact: (id: string) => void;
  onViewDetails: (professional: Professional) => void;
  isContacting?: boolean;
}) {
  const { t } = useTranslation();
  const initials = getInitials(professional.user.name);
  const typeChip = getTypeChipStyle(professional.type);
  const typeLabel = getProfessionalTypeLabel(t, professional.type);
  const hasRating = professional.reviewCount > 0;

  return (
    <div
      className={cn(
        'group rounded-[14px] bg-card p-4 flex gap-3.5 flex-col cursor-pointer',
        'shadow-[0_0_0_1px_var(--border)_inset] hover:shadow-[0_0_0_1px_rgba(223,201,147,.4)_inset]',
        'focus-within:shadow-[0_0_0_1px_rgba(223,201,147,.4)_inset] transition-shadow duration-200'
      )}
      onClick={() => onViewDetails(professional)}
    >
      <div className="flex gap-3.5">
        {/* uni-av */}
        {professional.user.avatarUrl ? (
          <img
            src={professional.user.avatarUrl}
            alt={professional.user.name}
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <span className="w-10 h-10 rounded-full overflow-hidden shrink-0">
            <DefaultAvatar name={professional.user.id || professional.user.name} />
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-1">
          {/* name + type chip */}
          <h4 className="font-serif font-medium text-[15px] text-foreground leading-snug">
            {professional.user.name}
            <span
              className="ml-2 font-mono text-[7.5px] uppercase tracking-widest align-middle px-2 py-0.5 rounded-full"
              style={{
                color: typeChip.color,
                background: typeChip.bg,
                boxShadow: `0 0 0 1px ${typeChip.ring} inset`,
              }}
            >
              {typeLabel}
            </span>
            {professional.isAvailable ? (
              <span className="ml-1.5 inline-flex items-center gap-1 font-mono text-[7.5px] uppercase tracking-widest text-green-600 dark:text-green-400 align-middle">
                <CheckCircle size={9} />
                {t('professionals.available', 'Disponível')}
              </span>
            ) : (
              <span className="ml-1.5 font-mono text-[7.5px] uppercase tracking-widest text-muted-foreground align-middle">
                {t('professionals.unavailable', 'Ocupado')}
              </span>
            )}
          </h4>

          {professional.title && (
            <p className="text-[11px] text-muted-foreground/80 truncate">
              {professional.title}
            </p>
          )}

          {professional.bio && (
            <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">
              {professional.bio}
            </p>
          )}

          {/* action row: pro-cta + rating */}
          <div className="flex items-center gap-2.5 pt-1 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onContact(professional.id);
              }}
              disabled={isContacting}
              className={cn(
                'inline-flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-widest',
                'px-2.5 py-1.5 rounded-full whitespace-nowrap transition-colors',
                'hover:bg-[rgba(223,201,147,.08)] disabled:opacity-50 disabled:cursor-not-allowed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              style={{ color: 'var(--ember-hot)', boxShadow: '0 0 0 1px rgba(223,201,147,.35) inset' }}
            >
              {isContacting ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <MessageSquare size={11} />
              )}
              {isContacting
                ? t('common.loading')
                : t('professionals.contact', 'Conversar')}
            </button>

            {hasRating && (
              <span
                className="font-mono text-[8px] tracking-wide whitespace-nowrap"
                style={{ color: 'var(--ember-hot)' }}
              >
                ★ {(professional.rating / 100).toFixed(1)} · {professional.reviewCount}{' '}
                {t('professionals.reviews', 'avaliações')}
              </span>
            )}

            {professional.hourlyRate && (
              <span className="font-mono text-[8px] tracking-wide text-muted-foreground whitespace-nowrap">
                {formatCurrency(professional.hourlyRate)}/hr
              </span>
            )}
          </div>

          {/* Specialty tags */}
          {professional.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {professional.specialties.slice(0, 3).map((specialty) => (
                <span
                  key={specialty}
                  className="text-[9px] font-mono px-2 py-0.5 rounded-full text-muted-foreground shadow-[0_0_0_1px_var(--border)_inset]"
                >
                  {specialty}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Card footer: languages + view profile */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex items-center">
          {professional.languages.length > 0 && (
            <LanguageFlags languages={professional.languages} max={3} />
          )}
        </div>
        <span className="flex items-center text-[10px] font-mono uppercase tracking-widest text-primary gap-0.5 group-hover:gap-1 transition-all shrink-0">
          {t('professionals.viewProfile', 'Perfil')}
          <ChevronRight size={12} />
        </span>
      </div>
    </div>
  );
}

// Skeleton for a single professional card
function ProfessionalCardSkeleton() {
  return (
    <div className="rounded-[14px] bg-card p-4 shadow-[0_0_0_1px_var(--border)_inset] animate-pulse space-y-3.5">
      <div className="flex gap-3.5">
        <div className="w-10 h-10 rounded-full bg-muted shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-3 w-40 bg-muted rounded" />
          <div className="h-3 w-24 bg-muted rounded" />
        </div>
      </div>
      <div className="flex gap-1.5 pl-[52px]">
        <div className="h-5 w-16 bg-muted rounded-full" />
        <div className="h-5 w-20 bg-muted rounded-full" />
      </div>
    </div>
  );
}

// Professional Detail Modal
function ProfessionalDetailModal({
  professional,
  open,
  onOpenChange,
  onContact,
  isContacting,
}: {
  professional: Professional;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContact: () => void;
  isContacting?: boolean;
}) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [showHireModal, setShowHireModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'reviews' | 'portfolio'>('about');

  const stars = getRatingStars(professional.rating);
  const initials = getInitials(professional.user.name);

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.getReviews(professional.id);
        setReviews(response.reviews);
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setIsLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [professional.id]);

  return (
    <>
      <ResponsiveModal open={open} onOpenChange={onOpenChange}>
        <ResponsiveModalContent className="flex max-h-[90vh] flex-col overflow-hidden p-0 sm:max-w-2xl">
          <ResponsiveModalTitle className="sr-only">
            {professional.user.name}
          </ResponsiveModalTitle>

          {/* Cover / gradient strip */}
          <div className="absolute top-0 left-0 w-full h-28 overflow-hidden pointer-events-none rounded-t-xl">
            <div className="w-full h-full bg-gradient-to-br from-primary/60 via-accent/45 to-primary/25" />
            <div
              className="absolute inset-0"
              style={{ background: 'radial-gradient(120% 140% at 15% 0%, color-mix(in oklab, var(--color-accent) 35%, transparent), transparent 60%)' }}
            />
          </div>

          {/* Header */}
          <div className="relative z-10 flex items-end gap-4 border-b border-border px-6 pt-12 pb-5">
            {professional.user.avatarUrl ? (
              <img
                src={professional.user.avatarUrl}
                alt={professional.user.name}
                className="w-20 h-20 rounded-2xl object-cover shrink-0 ring-4 ring-card shadow-xl"
              />
            ) : (
              <span className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 shadow-xl ring-4 ring-card">
                <DefaultAvatar name={professional.user.id || professional.user.name} />
              </span>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-serif font-semibold text-foreground">
                  {professional.user.name}
                </h2>
                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border bg-secondary text-secondary-foreground border-border uppercase tracking-wider">
                  {professional.type}
                </span>
                {professional.isAvailable && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400">
                    <CheckCircle size={9} />
                    {t('professionals.available', 'Disponível')}
                  </span>
                )}
              </div>
              <p className="text-sm text-primary font-medium mt-0.5">
                {professional.title}
              </p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                {professional.hourlyRate && (
                  <span className="font-mono font-bold text-accent">
                    {formatCurrency(professional.hourlyRate)}/hr
                  </span>
                )}
                <span aria-hidden>·</span>
                <div className="flex items-center gap-0.5 text-accent font-semibold">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={12}
                      className={cn(
                        i <= stars
                          ? 'fill-accent text-accent'
                          : 'text-muted-foreground/30'
                      )}
                    />
                  ))}
                  <span className="text-muted-foreground font-normal ml-1 font-mono">
                    ({professional.reviewCount}{' '}
                    {t('professionals.reviews', 'reviews')})
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border shrink-0">
            {(['about', 'reviews', 'portfolio'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex-1 px-4 py-3 text-[10px] font-mono font-semibold uppercase tracking-widest transition-colors relative',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activeTab === tab
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                {tab === 'about' && t('professionals.about', 'Sobre')}
                {tab === 'reviews' &&
                  `${t('professionals.reviews', 'Avaliações')} (${professional.reviewCount})`}
                {tab === 'portfolio' &&
                  t('professionals.portfolio', 'Portfólio')}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'about' && (
              <div className="space-y-6">
                {professional.bio && (
                  <div>
                    <h3 className="text-[10px] font-mono font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                      {t('professionals.bio', 'Bio')}
                    </h3>
                    <p className="text-sm text-foreground/80 leading-relaxed bg-muted/40 p-4 rounded-lg border border-border">
                      {professional.bio}
                    </p>
                  </div>
                )}

                {professional.specialties.length > 0 && (
                  <div>
                    <h3 className="text-[10px] font-mono font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                      {t('professionals.specialties', 'Especialidades')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {professional.specialties.map((specialty) => (
                        <span
                          key={specialty}
                          className="text-xs font-mono px-3 py-1 rounded-full border bg-secondary text-secondary-foreground border-border"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {professional.hourlyRate && (
                    <div className="bg-muted/40 rounded-lg border border-border p-4">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                        {t('professionals.hourlyRate', 'Valor por Hora')}
                      </p>
                      <p className="text-xl font-semibold text-foreground font-mono mt-1">
                        {formatCurrency(professional.hourlyRate)}/hr
                      </p>
                    </div>
                  )}
                  <div className="bg-muted/40 rounded-lg border border-border p-4">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                      {t('professionals.languages', 'Idiomas')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {professional.languages.map((lang) => {
                        const code = getLanguageFlag(lang);
                        return (
                          <div key={lang} className="flex items-center gap-1.5">
                            {code ? (
                              <span
                                className={`fi fi-${code}`}
                                style={{ width: '18px', height: '13px', borderRadius: '2px', display: 'inline-block' }}
                              />
                            ) : null}
                            <span className="text-sm font-medium text-foreground">{lang}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                {isLoadingReviews ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40">
                      {t('professionalsView.loadingReviews', 'Carregando avaliações…')}
                    </p>
                  </div>
                ) : reviews.length === 0 ? (
                  <EmptyState
                    icon={<Star size={32} />}
                    title={t('professionalsView.noReviewsTitle', 'Ainda sem avaliações.')}
                    description={t(
                      'professionalsView.noReviewsDescription',
                      'Este profissional ainda não recebeu avaliações de clientes.'
                    )}
                    className="py-12"
                  />
                ) : (
                  reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'portfolio' && (
              <PortfolioGallery professionalId={professional.id} />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-6 border-t border-border shrink-0">
            <button
              onClick={onContact}
              disabled={isContacting}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border',
                'text-foreground hover:bg-muted transition-colors disabled:opacity-50 text-sm font-medium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              {isContacting ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <MessageSquare size={15} />
              )}
              {t('professionals.sendMessage', 'Enviar Mensagem')}
            </button>
            <button
              onClick={() => setShowHireModal(true)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg',
                'bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <UserPlus size={15} />
              {t('professionals.hire', 'Contratar')}
            </button>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <HireModal
        professional={professional as any}
        open={showHireModal}
        onOpenChange={setShowHireModal}
        onSuccess={() => {
          setShowHireModal(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}

export function ProfessionalsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<ProfessionalType>('all');
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [contactingId, setContactingId] = useState<string | null>(null);
  const [selectedProfessional, setSelectedProfessional] =
    useState<Professional | null>(null);

  // Translate the static PT labels in professionalTypes using their i18n key mapping.
  const translatedProfessionalTypes = professionalTypes.map((type) => ({
    ...type,
    label: t(
      `professionalsView.${PROFESSIONAL_TYPE_I18N_KEYS[type.value]}`,
      type.label
    ),
  }));

  // Fetch professionals
  useEffect(() => {
    const fetchProfessionals = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedType !== 'all') params.set('type', selectedType);
        if (searchQuery) params.set('search', searchQuery);
        if (showAvailableOnly) params.set('available', 'true');

        const response = await fetch(`${API_BASE}/professionals?${params}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setProfessionals(data.professionals);
        }
      } catch (error) {
        console.error('Failed to fetch professionals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfessionals();
  }, [selectedType, searchQuery, showAvailableOnly]);

  const handleContact = async (professionalId: string) => {
    const professional = professionals.find((p) => p.id === professionalId);
    if (!professional) return;

    setContactingId(professionalId);
    try {
      // Create or get existing conversation with the professional's user
      const result = await api.createConversation(professional.userId);
      // cerimônia: o pergaminho da parceria
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'scroll', name: professional.user.name } }));
      // Navigate directly to that conversation in the messages view
      navigate(`/app/messages?c=${result.conversation.id}`);
      notify.success(
        t('professionals.conversationStarted', 'Conversa iniciada')
      );
    } catch (error) {
      console.error('Failed to start conversation:', error);
      notify.error(
        t('professionals.contactError', 'Falha ao iniciar conversa')
      );
    } finally {
      setContactingId(null);
    }
  };

  // Filter locally for instant feedback
  const filteredProfessionals = professionals.filter((p) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.user.name.toLowerCase().includes(query) ||
      p.title.toLowerCase().includes(query) ||
      p.bio?.toLowerCase().includes(query) ||
      p.specialties.some((s) => s.toLowerCase().includes(query))
    );
  });

  return (
    <>
      <PageShell width="xl">
        <div className="space-y-6">
        {/* Header — eyebrow + serif title, matching the Notifications header
            pattern (no icon, no top-level back button — navigation is via
            the bottom tab bar). */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="m-eyebrow">{t('nav.professionals', 'Profissionais')}</p>
            <h1 className="font-serif text-[22px] font-medium leading-snug text-foreground mt-1.5 truncate">
              {t('professionals.title', 'Encontrar Profissionais')}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border',
              'text-foreground hover:bg-muted transition-colors text-sm font-medium shrink-0',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}>
              <Briefcase size={15} />
              {t('professionals.postJob', 'Publicar Vaga')}
            </button>
          </div>
        </div>

        {/* Filters panel */}
        <div className="rounded-[14px] bg-card p-4 space-y-4 shadow-[0_0_0_1px_var(--border)_inset]">
          {/* Top row: search + availability toggle */}
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between w-full">
            {/* Search */}
            <div className="relative w-full md:w-80 shrink-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder={t(
                  'professionals.searchPlaceholder',
                  'Buscar por nome, habilidade ou especialidade...'
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-10 pr-4 py-2.5 rounded-lg bg-background border border-border',
                  'text-foreground placeholder:text-muted-foreground text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all'
                )}
              />
            </div>

            {/* Right-side controls */}
            <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
              {/* Type dropdown */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={cn(
                    'flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border border-border',
                    'bg-background text-foreground hover:bg-muted transition-colors text-sm min-w-36',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  <span className="truncate">
                    {translatedProfessionalTypes.find((tp) => tp.value === selectedType)?.label}
                  </span>
                  <ChevronDown
                    size={14}
                    className={cn(
                      'shrink-0 transition-transform duration-200',
                      dropdownOpen && 'rotate-180'
                    )}
                  />
                </button>
                {dropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-card border border-border rounded-lg shadow-lg z-20 py-1 overflow-hidden">
                      {translatedProfessionalTypes.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => {
                            setSelectedType(type.value);
                            setDropdownOpen(false);
                          }}
                          className={cn(
                            'w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors',
                            selectedType === type.value &&
                              'bg-muted text-primary font-semibold'
                          )}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Available Only Toggle */}
              <button
                onClick={() => setShowAvailableOnly(!showAvailableOnly)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-sm font-medium',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  showAvailableOnly
                    ? 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400'
                    : 'border-border text-foreground hover:bg-muted'
                )}
              >
                <CheckCircle size={14} />
                <span>{t('professionals.availableOnly', 'Disponíveis')}</span>
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-border" />

          {/* Specialty filter pills — horizontal scroll on mobile */}
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
              {t('professionals.filterByType', 'Filtrar por Tipo')}
            </p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 [mask-image:linear-gradient(to_right,white_85%,transparent)] md:[mask-image:none]">
              {translatedProfessionalTypes.map((type) => {
                const active = selectedType === type.value;
                return (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={cn(
                      'px-3.5 py-1.5 rounded-full font-mono text-[9px] uppercase tracking-widest whitespace-nowrap shrink-0 transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active
                        ? ''
                        : 'text-muted-foreground shadow-[0_0_0_1px_var(--border)_inset] hover:text-foreground'
                    )}
                    style={
                      active
                        ? {
                            color: 'var(--ember-hot)',
                            background: 'rgba(223,201,147,.08)',
                            boxShadow: '0 0 0 1px rgba(223,201,147,.35) inset',
                          }
                        : undefined
                    }
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results count */}
        {!isLoading && filteredProfessionals.length > 0 && (
          <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
            {filteredProfessionals.length}{' '}
            {filteredProfessionals.length === 1
              ? t('professionals.result', 'profissional encontrado')
              : t('professionals.results', 'profissionais encontrados')}
          </p>
        )}

        {/* Results grid */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <ProfessionalCardSkeleton key={i} />
            ))}
          </div>
        ) : filteredProfessionals.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card">
            <EmptyState
              icon={<User size={36} />}
              title={
                searchQuery
                  ? t(
                      'professionalsView.noResultsForSearch',
                      'Nenhum profissional corresponde a "{{searchQuery}}".',
                      { searchQuery }
                    )
                  : t(
                      'professionalsView.noResultsWithFilters',
                      'Nenhum profissional encontrado com esses filtros.'
                    )
              }
              description={t(
                'professionalsView.noResultsDescription',
                'Tente ajustar a busca, o tipo ou o filtro de disponibilidade.'
              )}
              className="py-16"
            />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProfessionals.map((professional, index) => (
              <Rise i={index} key={professional.id}>
                <ProfessionalCard
                  professional={professional}
                  index={index}
                  onContact={handleContact}
                  onViewDetails={setSelectedProfessional}
                  isContacting={contactingId === professional.id}
                />
              </Rise>
            ))}
          </div>
        )}
        </div>
      </PageShell>

      {/* Professional Detail Modal — rendered via portal, DOM position is irrelevant */}
      {selectedProfessional && (
        <ProfessionalDetailModal
          professional={selectedProfessional}
          open={true}
          onOpenChange={(o) => !o && setSelectedProfessional(null)}
          onContact={() => handleContact(selectedProfessional.id)}
          isContacting={contactingId === selectedProfessional.id}
        />
      )}
    </>
  );
}
